import {createFeatureSelector, createSelector, Store} from '@ngrx/store';
import {CharacterState} from './character.reducer';
import {Character, Player, Roll} from 'gamesis-story-client';
import {isGameMaster, selectMe} from '../players/player.selectors';
import {filter, tap} from 'rxjs/operators';
import {loadCharacterRollsById} from './character.actions';
import {Mode} from '../settings/settings.model';
import {selectCharacterId, selectMode} from '../settings/settings.selectors';

export const selectCharacterState = createFeatureSelector<CharacterState>('characters');

export const selectAllCharacters = createSelector(
    selectCharacterState,
    (state: CharacterState) => state.characters
);

export const selectMyCharacterIds = createSelector(
    selectCharacterState,
    (state: CharacterState) => state.myCharacters
);

export const selectActiveCharacters = createSelector(
    selectAllCharacters,
    (characters: Character[]) => characters
        .filter(character => character)
        .filter(character => !character.inactive)
);

export const selectInactiveCharacters = createSelector(
    selectAllCharacters,
    (characters: Character[]) => characters
        .filter(character => character)
        .filter(character => character.inactive)
);

export const selectMyCharacters = createSelector(
    selectActiveCharacters,
    selectMyCharacterIds,
    (active: Character[], mine: string[]) => {
        return active
            .filter(character => character)
            .filter(character => mine.includes(character.id));
    }
);

export const selectCharacterById = (id: string) => createSelector(
    selectAllCharacters,
    (characters: Character[]) => characters
        .filter(character => character)
        .find(character => character.id === id)
);

export const selectCharacterPlayedByMe = (character: Character) => createSelector(
    selectMe,
    isGameMaster,
    (me: Player, gm: boolean) => {
        if (!me || !character) {
            return false;
        }

        if (!character.players || [...character.players].length === 0) {
            return gm;
        }

        return [...character.players].includes(me.id);
    }
);

export const selectCanEditCharacter = (character: Character) => createSelector(
    selectCharacterPlayedByMe(character),
    isGameMaster,
    (playedByMe, isGm) => playedByMe || isGm
);

const selectRollsByCharacterIdInternal = (id: string) => createSelector(
    selectCharacterState,
    (state: CharacterState) => state.rolls[id]
);

export const selectRollsByCharacterId = (store: Store, id: string) => {
    return store.select(selectRollsByCharacterIdInternal(id)).pipe(
        tap((rolls: Roll[]) => {
            if (!rolls) {
                store.dispatch(loadCharacterRollsById({id: id}));
            }
        }),
        filter((rolls) => !!rolls) // Only pass through values once loaded
    );
};

export const selectCharactersLoading = createSelector(
    selectCharacterState,
    (state: CharacterState) => state.loading
);

export const selectCharactersError = createSelector(
    selectCharacterState,
    (state: CharacterState) => state.error
);

export const selectCharacterCount = createSelector(
    selectAllCharacters,
    (characters: Character[]) => characters.length
);

export const selectCurrentCharacter = createSelector(
    selectCharacterId,
    selectMode,
    selectActiveCharacters,
    (current, mode, characters) => {
        if (mode === Mode.story || mode === Mode.comment) {
            return null;
        }
        return characters.find(character => character.id === current);
    }
);

export const selectRolls = createSelector(
    selectCharacterState,
    selectCurrentCharacter,
    (state: CharacterState, character) => state.rolls[character.id]
);




